import { useUserMe } from "~/components/user/composables/useUser";
import { useApi } from "~/composables/useApi";
import { useEnvironment } from "~/composables/useEnvironment";
import type { Body as TeamMemberTrackEventBody } from "@asap/shared/schemas/analytics/team-member/track-event";
import { SharedError } from "@asap/shared/utils/error";

type TeamMemberEvent = TeamMemberTrackEventBody["event"];
type TeamMemberEventProperties = TeamMemberTrackEventBody["properties"];

export const usePosthog = () => {
  const { isProduction, isStaging } = useEnvironment();

  const isIdentifyingEnabled = isProduction || isStaging;
  const isTrackingEnabled = isProduction;

  const { teamMember } = useUserMe();
  const { invoke } = useApi();

  const identify = async () => {
    // Exit if identifying is not enabled
    if (!isIdentifyingEnabled) return;

    // Exit if team member is not defined
    if (!teamMember.value?.id) return;

    const response = await invoke({
      path: "analytics/team-member/identify",
      method: "POST",
      body: { id: teamMember.value.id },
    });

    if ("error" in response) {
      throw new SharedError("Failed to identify user", {
        context: { team_member_id: teamMember.value.id, response },
      });
    }
  };

  const trackEvent = async (event: TeamMemberEvent, properties?: TeamMemberEventProperties) => {
    // Exit if tracking is not enabled
    if (!isTrackingEnabled) return;

    // Exit if team member is not defined
    if (!teamMember.value?.id) return;

    const route = useRoute();

    const propertiesWithCurrentUrl = {
      ...properties,
      $current_url: route.fullPath,
    };

    const response = await invoke({
      path: "analytics/team-member/track-event",
      method: "POST",
      body: {
        id: teamMember.value.id,
        event,
        properties: propertiesWithCurrentUrl,
      },
    });

    if ("error" in response) {
      throw new SharedError("Failed to track event", {
        context: { team_member_id: teamMember.value.id, event, properties, response },
      });
    }
  };

  /** For tracking that must never get in the way of the action it observes: failures are dropped. */
  const trackEventSilently = async (...args: Parameters<typeof trackEvent>): Promise<void> => {
    try {
      await trackEvent(...args);
    } catch {
      return;
    }
  };

  const trackPageView = async (pageUrl: string) => {
    // Exit if tracking is not enabled
    if (!isTrackingEnabled) return;

    // Exit if team member is not defined
    if (!teamMember.value?.id) return;

    // Validate payload before sending

    const response = await invoke({
      path: "analytics/team-member/track-page-view",
      method: "POST",
      body: {
        id: teamMember.value.id,
        page_url: pageUrl,
      },
    });

    if ("error" in response) {
      throw new SharedError("Failed to track page view", {
        context: { team_member_id: teamMember.value.id, pageUrl, response: response },
      });
    }

    const { success } = response.data;

    return success;
  };

  const isFeatureFlagEnabled = async (name: string): Promise<boolean> => {
    const response = await invoke({
      path: "feature-flag/posthog",
      method: "GET",
      query: { name },
    });

    if ("error" in response) {
      throw new SharedError("Failed to check feature flag", {
        context: { name, response: response },
      });
    }

    const { enabled } = response.data;

    return enabled;
  };

  return { identify, trackEvent, trackEventSilently, trackPageView, isFeatureFlagEnabled };
};
